import { getOperationalEvents } from '../../lib/siteEvents';

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  if (!value) return 'date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'date pending';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatModeLabel = (mode) => {
  if (!mode) return 'Unset';

  return String(mode)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const buildSystemHealthAction = (label, tab, roomId = 'system-health', toolContext = null) => ({
  label,
  tab,
  roomId,
  toolContext,
});

export function buildSystemHealthRoomContext({
  room,
  documents = [],
  siteSettings = null,
  primaryAction,
  secondaryAction,
}) {
  const settingsReady = !!siteSettings;
  const upcomingEvents = getOperationalEvents(siteSettings?.upcoming_events);
  const recentDocuments = [...documents]
    .sort((a, b) => getTimeValue(b?.updated_at || b?.created_at) - getTimeValue(a?.updated_at || a?.created_at))
    .slice(0, 3);
  const onboardingDocuments = documents.filter((doc) => doc?.category === 'onboarding');
  const guidePartnerDocuments = documents.filter((doc) => doc?.category === 'guest_guide');
  const founding40Mode = siteSettings?.founding_40_public_mode || '';
  const maxFoundingMembers = Number(siteSettings?.max_founding_members) || 0;

  const upkeepInPlaceItems = [
    ...recentDocuments.slice(0, 2).map((doc) => ({
      id: doc.document_id || doc.id || doc.name || 'document',
      eyebrow: 'Library current',
      title: doc?.name || 'Untitled document',
      detail: [
        doc?.category ? `${formatModeLabel(doc.category)} category` : null,
        doc?.updated_at || doc?.created_at ? `updated ${formatDate(doc.updated_at || doc.created_at)}` : null,
      ].filter(Boolean).join(' · '),
      tone: 'next',
      location: 'In Documents',
      action: buildSystemHealthAction('Open Documents', 'documents'),
    })),
    ...upcomingEvents.slice(0, 1).map((event) => ({
      id: event.event_id || event.title || 'event',
      eyebrow: 'Calendar in place',
      title: event?.title || 'Upcoming event',
      detail: [
        event?.start_datetime ? formatDate(event.start_datetime) : null,
        event?.location_label || null,
      ].filter(Boolean).join(' · ') || 'Event details are already configured.',
      tone: 'steady',
      location: 'In Site Settings',
      action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
    })),
    settingsReady
      ? {
          id: 'system-health-mode',
          eyebrow: 'Public mode live',
          title: `Founding 40 is set to ${formatModeLabel(founding40Mode || 'join')}`,
          detail: maxFoundingMembers > 0
            ? `Capacity is currently set to ${maxFoundingMembers} dads.`
            : 'Core public-facing settings are loaded and reachable.',
          tone: 'steady',
          location: 'In Site Settings',
          action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
        }
      : null,
  ].filter(Boolean).slice(0, 4);

  const needsYourMoveItems = [
    !settingsReady
      ? {
          id: 'system-health-settings-review',
          eyebrow: 'Settings check',
          title: 'Review core site settings',
          detail: 'This room should stay dependable. If settings are missing or unreachable, the rest of the system gets harder to trust.',
          tone: 'attention',
          location: 'In Site Settings',
          action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
        }
      : null,
    documents.length === 0
      ? {
          id: 'system-health-no-documents',
          eyebrow: 'Library gap',
          title: 'No operating documents are in the library yet',
          detail: 'Add the core docs that keep onboarding, service, and member support easy to find when pressure rises.',
          tone: 'attention',
          location: 'In Documents',
          action: buildSystemHealthAction('Open Documents', 'documents'),
        }
      : null,
    settingsReady && upcomingEvents.length === 0
      ? {
          id: 'system-health-no-events',
          eyebrow: 'Calendar check',
          title: 'No upcoming events are currently configured',
          detail: 'That may be intentional, but this is where to confirm the calendar will not surprise you later.',
          tone: 'next',
          location: 'In Site Settings',
          action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
        }
      : null,
    guidePartnerDocuments.length === 0
      ? {
          id: 'system-health-guide-docs',
          eyebrow: 'Guide Partner materials',
          title: 'Guide Partner documents are still thin',
          detail: 'If partner workflows keep growing, make sure the document library can support invitation, onboarding, and prep cleanly.',
          tone: documents.length === 0 ? 'steady' : 'next',
          location: 'In Documents',
          action: buildSystemHealthAction('Open Documents', 'documents'),
        }
      : null,
    onboardingDocuments.length === 0
      ? {
          id: 'system-health-onboarding-docs',
          eyebrow: 'Onboarding library',
          title: 'Onboarding docs are not showing in the library yet',
          detail: 'A calm admin surface is easier to trust when onboarding materials are obvious and current.',
          tone: documents.length === 0 ? 'steady' : 'next',
          location: 'In Documents',
          action: buildSystemHealthAction('Open Documents', 'documents'),
        }
      : null,
  ].filter(Boolean).slice(0, 4);

  const suggestedActions = [];

  if (!settingsReady) {
    suggestedActions.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: 'Settle core site settings',
      detail: 'Start with the foundation that keeps public mode, events, and admin upkeep from drifting.',
      location: 'In Site Settings',
      action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
    });
  }

  if (documents.length === 0) {
    suggestedActions.push({
      tone: settingsReady ? 'attention' : 'next',
      eyebrow: suggestedActions.length > 0 ? 'Then' : 'Do first',
      title: 'Build the operating document library',
      detail: 'Create or upload the small set of documents the team will reach for most often under pressure.',
      location: 'In Documents',
      action: buildSystemHealthAction('Open Documents', 'documents'),
    });
  } else if (guidePartnerDocuments.length === 0 || onboardingDocuments.length === 0) {
    suggestedActions.push({
      tone: 'next',
      eyebrow: suggestedActions.length > 0 ? 'Then' : 'Do first',
      title: 'Fill the thinnest document coverage next',
      detail: guidePartnerDocuments.length === 0
        ? 'Guide Partner materials are the clearest documentation gap right now.'
        : 'Onboarding materials are the clearest documentation gap right now.',
      location: 'In Documents',
      action: buildSystemHealthAction('Open Documents', 'documents'),
    });
  }

  if (settingsReady && upcomingEvents.length === 0) {
    suggestedActions.push({
      tone: suggestedActions.length > 0 ? 'steady' : 'next',
      eyebrow: suggestedActions.length > 0 ? 'Keep tidy' : 'Do first',
      title: 'Confirm whether an upcoming event should be scheduled',
      detail: 'If nothing is supposed to be on the calendar, this can stay quiet. If something should be there, set it before it becomes a scramble.',
      location: 'In Site Settings',
      action: buildSystemHealthAction('Open Site Settings', 'sitesettings'),
    });
  }

  if (!suggestedActions.length) {
    suggestedActions.push({
      tone: 'steady',
      eyebrow: 'Quiet',
      title: 'Keep the operating surface current and dependable',
      detail: 'Nothing looks especially loose right now, so this room can stay focused on gentle maintenance instead of urgent cleanup.',
      location: 'In Admin Systems',
      action: buildSystemHealthAction('Open Documents', 'documents'),
    });
  }

  const statusTone = !settingsReady || documents.length === 0
    ? 'attention'
    : upcomingEvents.length === 0 || guidePartnerDocuments.length === 0 || onboardingDocuments.length === 0
      ? 'next'
      : 'steady';

  const statusLabel = !settingsReady || documents.length === 0
    ? 'Needs attention'
    : upcomingEvents.length === 0 || guidePartnerDocuments.length === 0 || onboardingDocuments.length === 0
      ? 'Tidy next'
      : 'Steady';

  return {
    id: room.id,
    variant: 'system-health',
    statusTone,
    statusLabel,
    startTitle: !settingsReady
      ? 'Settle the system foundation before drift turns into confusion'
      : documents.length === 0
        ? 'Build the operating library before the admin surface gets harder to trust'
        : 'Keep the operating surface clean, current, and dependable',
    startDetail: 'This room is for the quiet upkeep that makes the rest of Dad Partners easier to run: documents, settings, and operational details that should stay orderly before they become distracting. Live incidents and watcher status belong in Monitoring, not here.',
    signals: [
      {
        tone: settingsReady ? 'steady' : 'attention',
        text: settingsReady
          ? 'Core site settings are loaded and reachable.'
          : 'Core site settings still need a review pass.',
      },
      {
        tone: documents.length > 0 ? 'next' : 'attention',
        text: documents.length > 0
          ? `${pluralize(documents.length, 'document')} are currently in the operating library`
          : 'No operating documents are currently in the library.',
      },
      {
        tone: upcomingEvents.length > 0 ? 'steady' : 'next',
        text: upcomingEvents.length > 0
          ? `${pluralize(upcomingEvents.length, 'upcoming event')} are already configured`
          : 'No upcoming events are currently configured.',
      },
    ],
    metrics: [
      { label: 'Documents', value: documents.length, detail: 'Files and links keeping the operation organized', tone: documents.length > 0 ? 'next' : 'attention' },
      { label: 'Upcoming events', value: upcomingEvents.length, detail: 'Operational events currently on the calendar', tone: upcomingEvents.length > 0 ? 'steady' : 'next' },
      { label: 'Public mode', value: settingsReady ? formatModeLabel(founding40Mode || 'join') : 'Check', detail: 'Current Founding 40 public setting', tone: settingsReady ? 'steady' : 'attention' },
    ],
    upkeepInPlaceItems,
    needsYourMoveItems,
    suggestedActions,
    primaryAction,
    secondaryAction,
  };
}
